import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, KeyRound, Loader2, Eye, EyeOff, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChangePasswordModal = ({ open, onClose, token }) => {
  const [pwData, setPwData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!pwData.current_password || !pwData.new_password || !pwData.confirm_password) {
      toast.error('All password fields are required');
      return;
    }
    if (pwData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (pwData.new_password !== pwData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwData.current_password,
        new_password: pwData.new_password
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password changed successfully');
      setPwData({ current_password: '', new_password: '', confirm_password: '' });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally { setLoading(false); }
  };

  const handleClose = () => {
    setPwData({ current_password: '', new_password: '', confirm_password: '' });
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="change-password-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-violet-600" />
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Change Password</h3>
          </div>
          <button onClick={handleClose} className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-testid="close-password-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="modal_current_password">Current Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input id="modal_current_password" type={showCurrent ? 'text' : 'password'} value={pwData.current_password} onChange={(e) => setPwData(prev => ({ ...prev, current_password: e.target.value }))} placeholder="Enter current password" className="pl-10 pr-10" data-testid="current-password-input" />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="modal_new_password">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input id="modal_new_password" type={showNew ? 'text' : 'password'} value={pwData.new_password} onChange={(e) => setPwData(prev => ({ ...prev, new_password: e.target.value }))} placeholder="Enter new password (min. 6 characters)" className="pl-10 pr-10" data-testid="new-password-input" />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="modal_confirm_password">Confirm New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input id="modal_confirm_password" type={showConfirm ? 'text' : 'password'} value={pwData.confirm_password} onChange={(e) => setPwData(prev => ({ ...prev, confirm_password: e.target.value }))} placeholder="Confirm new password" className="pl-10 pr-10" data-testid="confirm-password-input" />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
        <div className="p-5 border-t flex gap-3">
          <Button variant="outline" onClick={handleClose} className="flex-1" data-testid="cancel-password-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="change-password-btn">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Changing...</> : <><KeyRound className="mr-2 h-4 w-4" />Change Password</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
